import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  postResolveAppeal,
  useAdminAppealsCache,
  type ResolveAppealVariables,
} from '../../../api/admin-appeals';
import { publishToast } from '../../../components/feedback';

/**
 * Encapsulates the resolve orchestration for the appeal detail drawer
 * (AUT-130): fires the terminal, money-moving POST, a success / error
 * toast, and invalidates the `['admin','appeals']` cache so the list +
 * drawer refetch (the `AppealDto` envelope is NOT used to patch the row —
 * its shape differs from the admin projections). The action is role +
 * permission (`resolve_appeals`) + audit gated server-side (AUT-138
 * removed per-action TOTP step-up).
 */
export interface UseResolveAppealResult {
  /** True while the mutation is in flight. */
  busy: boolean;
  /** Run a resolve; resolves true on success, false on error. */
  run: (variables: ResolveAppealVariables) => Promise<boolean>;
}

export const useResolveAppeal = (): UseResolveAppealResult => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const { invalidate } = useAdminAppealsCache();

  const run = async (variables: ResolveAppealVariables): Promise<boolean> => {
    setBusy(true);
    try {
      await postResolveAppeal(variables);
      await invalidate();
      publishToast({
        severity: 'success',
        durationMs: 4000,
        message: t('appeals.resolveDialog.successToast'),
      });
      return true;
    } catch {
      publishToast({
        severity: 'error',
        durationMs: 6000,
        message: t('appeals.resolveDialog.errorToast'),
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
};
